import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Database } from 'lucide-react'

export function ProcessingHeader({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div className="flex items-start gap-3">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-lg"
        style={{ background: 'rgba(132, 204, 22, 0.12)' }}
      >
        <Database className="h-5 w-5" style={{ color: 'rgb(101, 163, 13)' }} />
      </div>
      <div>
        <h2
          className="text-lg font-bold"
          style={{ color: 'var(--text)', fontFamily: 'var(--font-display, Syne, sans-serif)' }}
        >
          {t('detail.processing.title')}
        </h2>
        <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
          {t('detail.processing.description')}
        </p>
      </div>
    </div>
  )
}

interface MutationErrorBannerProps {
  message: string
  onDismiss: () => void
}

export function MutationErrorBanner({
  message,
  onDismiss,
}: MutationErrorBannerProps): ReactElement {
  return (
    <div
      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-950/30"
      role="alert"
    >
      <div className="items-centre flex justify-between gap-4">
        <p className="text-sm text-red-700 dark:text-red-400">{message}</p>
        <button
          onClick={onDismiss}
          className="text-xs text-red-500 transition-colors hover:text-red-700 dark:text-red-400"
          aria-label="Dismiss error"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

interface CorruptDialogProps {
  corruptReason: string
  setCorruptReason: (v: string) => void
  flagging: boolean
  onCancel: () => void
  onConfirm: () => void
  t: TFunction<'cases'>
}

export function CorruptDialog(props: CorruptDialogProps): ReactElement {
  const { corruptReason, setCorruptReason, flagging, onCancel, onConfirm, t } = props
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-900">
        <h2
          className="mb-4 text-base font-semibold text-slate-900 dark:text-slate-100"
          style={{ fontFamily: 'var(--font-display, Syne, sans-serif)' }}
        >
          {t('detail.processing.flagCorrupt')}
        </h2>
        <label
          htmlFor="corrupt-reason"
          className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {t('detail.processing.corruptReason')}
        </label>
        <textarea
          id="corrupt-reason"
          value={corruptReason}
          onChange={(event) => {
            setCorruptReason(event.target.value)
          }}
          rows={3}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-[var(--accent,#4e5e82)] focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={flagging}
            className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
            style={{ background: 'var(--accent, #4e5e82)' }}
          >
            {flagging ? t('detail.processing.flagging') : t('detail.processing.flagCorrupt')}
          </button>
        </div>
      </div>
    </div>
  )
}
